import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterPanel } from '@/components/common/AdvancedFilter';
import { activeExamBoardsQueryOptions } from '@/features/examBoards/api/examBoards.api';
import { classSubjectsQueryOptions } from '../api/question.api';
import { getFilterPanelProps, type UrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { getDateFilterField, getExamBoardFilterField } from '@/utils/filterFields';
import { useCollectorFilterField } from '../hooks/useCollectorFilterField';
import { useInstituteFilterField } from '../hooks/useInstituteFilterField';
import { useProjectFilterField } from '../hooks/useProjectFilterField';
import { getCollectionFilterField, getNewPapersFilterField } from '../utils/questionFilterFields';

const TARGET_DATE_FILTER_KEY = 'targetDate';
const BOARD_FILTER_KEY = 'boardId';

interface MonitorFilterPanelProps {
  filters: UrlSyncedFilters;
  classId?: number | undefined;
}

export function MonitorFilterPanel({ filters, classId }: MonitorFilterPanelProps) {
  const { filterValues } = filters;

  const { data: boards } = useQuery(activeExamBoardsQueryOptions());
  const selectedBoardId = Number(filterValues[BOARD_FILTER_KEY] ?? '');
  const selectedMemberId = Number(filterValues.userId ?? '');

  const projectField = useProjectFilterField();
  const collectorField = useCollectorFilterField(
    filterValues.userId ?? '',
    Number.isFinite(selectedBoardId) && selectedBoardId > 0 ? selectedBoardId : undefined,
  );
  const { data: classSubjects } = useQuery(classSubjectsQueryOptions(classId, true));
  const instituteField = useInstituteFilterField(
    filterValues.instituteId ?? '',
    Number.isFinite(selectedMemberId) && selectedMemberId > 0 ? selectedMemberId : undefined,
  );

  return (
    <AdvancedFilterPanel
      fields={[
        projectField,
        getExamBoardFilterField(boards, BOARD_FILTER_KEY),
        collectorField,
        instituteField,
        {
          key: 'subjectIds',
          label: 'Collection Subject',
          type: 'multiSelect' as const,
          placeholder: 'All subjects',
          options: (classSubjects ?? []).map((pair) => ({
            value: String(pair.subjectId),
            label: pair.subjectName,
            description: pair.subjectLocal ?? undefined,
          })),
        },
        getDateFilterField('Target Date On or Before', TARGET_DATE_FILTER_KEY),
        getCollectionFilterField(),
        getNewPapersFilterField(),
      ]}
      {...getFilterPanelProps(filters)}
    />
  );
}
